import type { MessageDescriptor } from "@lingui/core";

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";

import {
  apiGetQuestionnairesStatsQuestionnaireTypeOverall,
  QuestionnaireTypes,
} from "../../../api";
import { ApiConnector } from "../../../api/api-connector";
import { Button } from "../../../components/ui-kit/button";
import { Select } from "../../../components/ui-kit/fields/select";
import { resolveEnumLabel } from "../../../components/ui-kit/fields/utils";
import { Spinner } from "../../../components/ui-kit/spinner";
import {
  type Column,
  DataTable,
} from "../../../components/ui-kit/tables/data-table";
import { questionnaireTypes } from "../../../constants/enum-to-text";
import { uniques } from "../../../utils/utils";
import {
  QuestionnaireStatsModel,
  type QuestionnaireStatsRow,
} from "./questionnaire-stats-model";

export const QuestionnaireAnalytics = observer(
  function QuestionnaireAnalytics(props: { api: ApiConnector }) {
    const [type, setType] = useState(QuestionnaireTypes.eq_5d_5l);
    const [strategy, setStrategy] = useState(StatsStrategy.last);
    const [showStatsFor, setShowStatsFor] = useState<{
      type: QuestionnaireTypes;
      strategy: StatsStrategy;
    } | null>(null);

    return (
      <div>
        <div>
          <label>
            <Trans>Questionnaire:</Trans>{" "}
            <Select
              values={questionnaireTypes}
              value={type}
              onChange={(newValue) => {
                setType(newValue);
              }}
            />
          </label>{" "}
          <label>
            <Trans>Stats:</Trans>{" "}
            <Select
              values={statsStrategies}
              value={strategy}
              onChange={(newValue) => {
                setStrategy(newValue);
              }}
            />
          </label>
          <br className="h-5" />
          <Button
            variant="primary"
            onClick={() => {
              setShowStatsFor({ type, strategy });
            }}
          >
            <Trans>Load stats</Trans>
          </Button>
        </div>

        {showStatsFor && (
          <QuestionnaireStats
            type={showStatsFor.type}
            strategy={showStatsFor.strategy}
            api={props.api}
          />
        )}
      </div>
    );
  },
);

const QuestionnaireStats = observer(function QuestionnaireStats(props: {
  type: QuestionnaireTypes;
  strategy: StatsStrategy;
  api: ApiConnector;
}) {
  const { t } = useLingui();
  const [model, setModel] = useState<QuestionnaireStatsModel | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    void (async () => {
      const res = await apiGetQuestionnairesStatsQuestionnaireTypeOverall({
        path: { questionnaire_type: props.type },
        query: { per_patient: props.strategy },
      });

      if (cancelled) return;

      const newModel = new QuestionnaireStatsModel(
        [
          ...res.data.responses_stats_per_patient,
          {
            patient_id: null,
            per_question: res.data.responses_stats_summary,
          }, // Summary
        ],
        props.api.permissions,
        props.api.requireUserUUID(),
      );

      newModel.init();

      setModel(newModel);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [props.type, props.strategy, props.api]);

  const columns = useMemo((): Column<QuestionnaireStatsRow>[] => {
    if (!model) {
      return [];
    }

    const columns: Column<QuestionnaireStatsRow>[] = [
      {
        header: <Trans>Patient ID</Trans>,
        cell: ({ item }) => item.patient_id ?? t`Summary`,
      },
    ];

    const questionKeys = uniques(
      model.data.flatMap((item) => Object.keys(item.per_question)),
    ).sort();

    columns.push(
      ...questionKeys.map(
        (key): Column<QuestionnaireStatsRow> => ({
          header: key,
          cell: ({ item }) => item.per_question[key],
        }),
      ),
    );

    return columns;
  }, [model]);

  return (
    <div>
      <h4 className="mt-5 mb-5 font-bold">
        <Trans>
          {resolveEnumLabel(statsStrategies[props.strategy])} stats for{" "}
          {questionnaireTypes[props.type]}:
        </Trans>
      </h4>

      {loading ? (
        <Spinner size={20} />
      ) : model && model.data.length > 0 ? (
        <DataTable columns={columns} model={model} itemKeyField="patient_id" />
      ) : (
        <Trans>No responses yet</Trans>
      )}
    </div>
  );
});

export enum StatsStrategy {
  last = "last",
  first = "first",
  aggregate = "aggregate",
}

export const statsStrategies: Record<StatsStrategy, MessageDescriptor> = {
  [StatsStrategy.last]: msg`Last`,
  [StatsStrategy.first]: msg`First`,
  [StatsStrategy.aggregate]: msg`Aggregate`,
};
